// Epoch 53: Outreach Credit Balance Widget
// Displays user's outreach credit balance on dashboard
// Date: October 28, 2025

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/client';
import { Link } from 'react-router-dom';

interface CreditBalanceData {
  balance: number;
  lifetime_purchased: number;
  lifetime_used: number;
  pricing_plan: string;
}

interface CreditBalanceWidgetProps {
  variant?: 'default' | 'compact';
}

const CreditBalanceWidget: React.FC<CreditBalanceWidgetProps> = ({ variant = 'default' }) => {
  const [balanceData, setBalanceData] = useState<CreditBalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setError('Not authenticated');
          return;
        }

        // Check if user is on usage-based pricing
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('pricing_plan, role')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          console.error('Error fetching user data:', userError);
          setError('Failed to fetch user data');
          return;
        }

        // Show credit balance for Platform, usage-based, and trial users
        // Platform users ($30/month) purchase credits separately
        // Trial users get 100 free credits
        // Legacy flat_rate users ($400/month) don't use credits
        if (userData.pricing_plan !== 'platform' && userData.pricing_plan !== 'usage_based' && userData.pricing_plan != null) {
          // For non-credit users (flat_rate, etc.), don't show the widget
          setBalanceData(null);
          return;
        }

        // Fetch balance from Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-outreach-balance`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch balance: ${response.statusText}`);
        }

        const data = await response.json();
        setBalanceData({
          balance: data.balance,
          lifetime_purchased: data.lifetime_purchased,
          lifetime_used: data.lifetime_used,
          pricing_plan: data.pricing_plan,
        });
      } catch (error) {
        console.error('Error fetching credit balance:', error);
        setError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, []);

  // Don't render anything if not on usage-based pricing
  if (!loading && !balanceData) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Outreach Credits</h3>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Outreach Credits</h3>
        <p className="text-sm text-red-600">Error: {error}</p>
      </div>
    );
  }

  if (!balanceData) {
    return null;
  }

  const { balance, lifetime_purchased, lifetime_used } = balanceData;
  const usagePercentage = lifetime_purchased > 0
    ? Math.round((lifetime_used / lifetime_purchased) * 100)
    : 0;

  // Determine balance color
  const getBalanceColor = () => {
    if (balance === 0) return 'text-red-600';
    if (balance < 100) return 'text-orange-600';
    if (balance < 500) return 'text-yellow-600';
    return 'text-green-600';
  };

  const isPaidCreditUser = balanceData.pricing_plan === 'platform' || balanceData.pricing_plan === 'usage_based';

  if (variant === 'compact') {
    return (
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className={`text-3xl font-bold ${getBalanceColor()}`}>
            {balance.toLocaleString()}
          </span>
          <span className="text-sm text-gray-500">credits</span>
        </div>
        <div className="text-xs text-gray-500">
          {lifetime_used.toLocaleString()} used of {lifetime_purchased.toLocaleString()} purchased
        </div>
        <Link
          to={isPaidCreditUser ? "/credits" : "/pricing"}
          className="inline-block text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          {isPaidCreditUser ? "Manage Credits →" : "Upgrade for more →"}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Outreach Credits</h3>
        <span className="text-2xl">💳</span>
      </div>

      <div className="space-y-4">
        {/* Current Balance */}
        <div>
          <div className="flex items-baseline space-x-2">
            <span className={`text-3xl font-bold ${getBalanceColor()}`}>
              {balance.toLocaleString()}
            </span>
            <span className="text-gray-500">credits available</span>
          </div>
        </div>

        {/* Usage Bar */}
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Usage</span>
            <span>{usagePercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{lifetime_used.toLocaleString()} used</span>
            <span>{lifetime_purchased.toLocaleString()} purchased</span>
          </div>
        </div>

        {/* Low Balance Warning */}
        {balance < 100 && balance > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ Low balance! Consider purchasing more credits.
            </p>
          </div>
        )}

        {balance === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              ❌ No credits remaining! Purchase credits to continue.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2 pt-2">
          <Link
            to="/credits"
            className="flex-1 text-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
          >
            Buy Credits
          </Link>
          <Link
            to="/credits"
            className="px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium"
          >
            View History
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreditBalanceWidget;
